<template>
    <div v-if="patent" id="function-bar" class="function-bar">
        <div class="function-bar-background">
            <LangSwitcher
                v-if="side === 'left'"
                :patent="patent"
                :patentId="patentId"
                :side="side"
                class="function-bar__translate"
            />
            <FunctionGroup
                v-if="acls.has('pdfDownload') || acls.has('addToWorkspace')"
                :patentId="patentId"
                :patent="patent"
                :acls="acls"
            >
                <template #prepend>
                    <PtDropdownItem v-if="acls.has('pdfDownload')" key="pdfDownload" :command="() => undefined">
                        <ElasticButton
                            :asyncClick="() => pdfAct()"
                            :title="$t('Download')"
                            :disabledTooltip="true"
                            :disabled="false"
                            class="pendo-track__function-bar__pdf-download"
                        >
                            <template #icon>
                                <PtIcon icon="SolidExportPdf" />
                            </template>
                            <template #text>
                                {{ $t('Download') }}
                            </template>
                        </ElasticButton>
                    </PtDropdownItem>
                    <PtDropdownItem v-if="acls.has('addToWorkspace')" key="saveToWorkspace" :command="() => undefined">
                        <SaveToWorkspaceComplex :key="`s2ws-${patentId}`" :patentId="patentId" :disabledTooltip="true" />
                    </PtDropdownItem>
                </template>
            </FunctionGroup>
        </div>
    </div>
</template>

<script>
import SaveToWorkspaceComplex from './SaveToWorkspaceComplex';
import { downloadFileByIframe } from '@/utils';
import { AlertMixin } from 'library-@/mixins';
import get from 'lodash/get';
import ElasticButton from './ElasticButton';
import { DropdownItem } from 'patsnap-ui';
import { Icon } from 'patsnap-biz';
import '@patsnap-ui/icon/assets/solid/ExportPdf.svg';
import LangSwitcher from './lang-switcher/LangSwitcher';
import FunctionGroup from './FunctionGroup';
import { getSRPPdfApi } from 'library-@/apis/core_basic_api/srp';
import { usePermission } from '@/composables/use-permission';
import { usePatentBasic } from '@/composables/use-patent-basic';

const noPdfPatentIdCacheSet = new Set();

export default {
    name: 'FunctionBarCompare',
    components: {
        PtIcon: Icon,
        PtDropdownItem: DropdownItem,
        SaveToWorkspaceComplex,
        LangSwitcher,
        ElasticButton,
        FunctionGroup,
    },
    mixins: [AlertMixin],
    props: {
        patentId: {
            type: String,
            required: true,
        },
        side: {
            type: String,
            required: true,
        },
        viewParams: {
            type: Object,
            required: true,
        },
    },
    setup() {
        const {
            permissions,
        } = usePermission();

        const {
            getPatentBase,
        } = usePatentBasic();

        return {
            permissions,
            getPatentBase,
        };
    },
    data() {
        return {
            patent: {},
        };
    },
    computed: {
        acls() {
            return Object.keys(this.permissions).reduce((aclSet, curKey) => {
                if (this.permissions[curKey]) aclSet.add(curKey);
                return aclSet;
            }, new Set());
        },
    },
    watch: {
        patentId: {
            async handler(newId) {
                this.patent = await this.getPatentBase(newId);
            },
            immediate: true,
        },
    },
    methods: {
        async pdfAct() {
            const noPdfReturn = {
                disabled: true,
                tip: this.$t('pdf-download.pdf-not-exist'),
            };

            if (noPdfPatentIdCacheSet.has(this.patentId)) {
                this.warnAlert(this.$t('pdf-download.pdf-not-exist'));
                return noPdfReturn;
            }
            await getSRPPdfApi({
                patentId: this.patentId,
                lang: get(this, 'patent.TITLE_LANG', this.$lang),
            }).then(res => {
                this.$eventHub.$emit('saveDownloadPatent', this.patentId);
                const url = get(res, 'data.data.PDF_D');
                if (url) {
                    downloadFileByIframe(url);
                } else {
                    noPdfPatentIdCacheSet.add(this.patentId);
                    this.warnAlert(this.$t('pdf-download.pdf-not-exist'));
                    return noPdfReturn;
                }
                window.rvTrack('Downloaded Patent', {
                    source: '/patent-view',
                    pub_num: this.patent.PN,
                });
            }).catch(err => {
                this.warnAlert(err._error_msg);
            });
            return undefined;
        },
    },
};
</script>

<style lang="scss">
@import './function-bar.scss';
.compare-view {
    .function-bar__translate {
        margin-right: 4px;
        .elstc-btn {
            height: 32px;
            line-height: 32px;
            padding: 0 .6em 0 .4em;
        }
    }
}
</style>
